"use client";

import { RichText as PayloadRichText } from "@payloadcms/richtext-lexical/react";
import type { SerializedEditorState } from "@payloadcms/richtext-lexical/lexical";
import { cn } from "@/lib/utils";

/**
 * RichText Component
 * 
 * A wrapper around Payload's official RichText component that handles
 * null/undefined content and provides consistent styling options.
 * This component converts Lexical editor state to semantic HTML.
 */

interface RichTextProps {
  /** The serialized Lexical editor state from Payload */
  content: SerializedEditorState | null | undefined;
  /** Optional CSS classes to apply to the container */
  className?: string;
  /** Whether to disable the container div wrapper */
  disableContainer?: boolean;
  /** Whether to disable indentation */
  disableIndent?: boolean | string[];
  /** Whether to disable text alignment */
  disableTextAlign?: boolean | string[];
}

export function RichText({
  content,
  className,
  disableContainer = false,
  disableIndent = false,
  disableTextAlign = false,
}: RichTextProps) {
  // Don't render anything if there's no content
  if (!content) return null;

  // Check if content has actual nodes
  if (!content.root?.children?.length) return null;

  return (
    <PayloadRichText
      data={content}
      className={cn(
        // Custom rich text styles (defined in globals.css)
        "rich-text max-w-none",
        // Allow customization via className prop
        className
      )}
      disableContainer={disableContainer}
      disableIndent={disableIndent}
      disableTextAlign={disableTextAlign}
    />
  );
}